import React from "react";
import { C, radii, space, type } from "../../styles/tokens";

const TONES = {
  warning: { bg: C.warningTint, text: C.warning },
  success: { bg: C.successTint, text: C.success },
  danger: { bg: C.dangerTint, text: C.danger },
  info: { bg: C.infoTint, text: C.navy },
  neutral: { bg: C.surfaceSunken, text: C.navy },
};

/**
 * StatusTiles (docs/BRAND_MANUAL.md, section 8)
 * Pastel status backgrounds, round white icon circle, count in title size with tabular figures.
 * tiles: [{ label, value, detail, tone, Icon, onClick }]
 */
export function StatusTiles({ tiles = [], layout = "grid", style = {} }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns:
          layout === "rows"
            ? "1fr"
            : "repeat(auto-fit, minmax(180px, 1fr))",
        gap: space.md, // 12px
        marginBottom: space.lg,
        ...style,
      }}
    >
      {tiles.map(({ label, value, detail, tone = "info", Icon, onClick }, idx) => {
        const toneStyle = TONES[tone] || TONES.info;
        const isClickable = Boolean(onClick);

        return (
          <div
            key={label || idx}
            role={isClickable ? "button" : undefined}
            tabIndex={isClickable ? 0 : undefined}
            onClick={onClick}
            onKeyDown={(e) => {
              if (isClickable && (e.key === "Enter" || e.key === " ")) {
                e.preventDefault();
                onClick();
              }
            }}
            style={{
              backgroundColor: toneStyle.bg,
              borderRadius: radii.tile, // 12px
              padding: space.md,
              display: "flex",
              alignItems: "center",
              gap: space.md,
              cursor: isClickable ? "pointer" : "default",
              transition: "transform 0.1s ease-out, filter 0.15s ease-out",
              outline: "none",
            }}
          >
            {/* Round white icon circle */}
            <div
              style={{
                width: 40,
                height: 40,
                borderRadius: radii.pill,
                backgroundColor: C.surface,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                color: toneStyle.text,
              }}
            >
              {Icon && <Icon size={20} strokeWidth={2} />}
            </div>

            <div style={{ flex: 1, minWidth: 0 }}>
              <div
                style={{
                  color: C.navy,
                  fontSize: type.title.fontSize,
                  lineHeight: type.title.lineHeight,
                  fontWeight: type.title.fontWeight,
                  fontVariantNumeric: "tabular-nums",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {value}
              </div>
              <div
                style={{
                  color: C.textStrong,
                  fontSize: type.label.fontSize,
                  lineHeight: type.label.lineHeight,
                  fontWeight: type.label.fontWeight,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {label}
              </div>
              {detail && (
                <div
                  style={{
                    color: C.textMuted,
                    fontSize: type.caption.fontSize,
                    lineHeight: type.caption.lineHeight,
                    marginTop: 2,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {detail}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}
